import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Pagination, Table, TableColumn } from 'common/components';
import { useGetWorkflowPhoneNumbers } from 'services/hooks/queries';
import {
    CommunicationMedium,
    WorkflowPhoneNumber,
} from 'services/api/getWorkflowPhoneNumbers';

import styles from '../AgenticWorkflowDetail.module.scss';

const COMMUNICATION_MEDIUM_LABELS: Record<CommunicationMedium, string> = {
    CALL: 'Call',
    SMS: 'SMS',
    BOTH: 'Call & SMS',
};

const formatProvider = (provider: string): string =>
    provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : '—';

const formatCommunicationMedium = (medium: CommunicationMedium | string): string =>
    COMMUNICATION_MEDIUM_LABELS[medium as CommunicationMedium] ?? medium ?? '—';

const WorkflowPhoneNumbersPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const [currentPage, setCurrentPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const { phoneNumbers, isPhoneNumbersFetching } = useGetWorkflowPhoneNumbers({
        workflowId: id || '',
        currentPage,
        perPage: rowsPerPage,
    });

    const rows = phoneNumbers?.data ?? [];
    const totalPages = phoneNumbers?.pagination?.totalPages || 1;
    const currentPageFromAPI = phoneNumbers?.pagination?.currentPage || currentPage;
    const perPageFromAPI = phoneNumbers?.pagination?.perPage || rowsPerPage;

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const columns: TableColumn<WorkflowPhoneNumber>[] = useMemo(
        () => [
            {
                key: 'phoneNumber',
                title: 'Phone Number',
                dataIndex: 'phoneNumber',
                width: 180,
            },
            {
                key: 'provider',
                title: 'Provider',
                width: 140,
                render: (_, record) => formatProvider(record.provider),
            },
            {
                key: 'outboundTrunkSid',
                title: 'Outbound Trunk SID',
                dataIndex: 'outboundTrunkSid',
                width: 220,
            },
            {
                key: 'communicationMedium',
                title: 'Communication Medium',
                width: 180,
                render: (_, record) => formatCommunicationMedium(record.communicationMedium),
            },
        ],
        []
    );

    return (
        <div className='flex flex-col gap-2xl' data-testid='workflow-phone-numbers-page'>
            <Table<WorkflowPhoneNumber>
                data={rows}
                columns={columns}
                emptyText='No phone numbers assigned to this workflow.'
                rowKey={(record) => `${record.phoneNumber}-${record.outboundTrunkSid}`}
                showHeader
                variant='secondary'
                hoverable
                size='medium'
                isLoading={isPhoneNumbersFetching}
            />
            <div className='flex justify-end items-center flex-wrap gap-sm'>
                <Pagination
                    currentPage={currentPageFromAPI}
                    totalPages={totalPages}
                    rowsPerPage={perPageFromAPI}
                    onPageChange={setCurrentPage}
                    onRowsPerPageChange={(newRowsPerPage) => {
                        setRowsPerPage(newRowsPerPage);
                        setCurrentPage(1);
                    }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className={styles.agenticPagination}
                />
            </div>
        </div>
    );
};

export default WorkflowPhoneNumbersPage;
